<template>
    <dialog ref="dialogRef" class="search-task-dialog bg-transparent th-modal">
        <form method="dialog" class="th-modal-box max-w-[680px] p-0">
            <!-- Header: Search Input -->
            <div class="sticky top-0 bg-white z-10 px-6 pt-6 pb-4 ">
                <div class="flex items-center gap-3">
                    <input
                        v-model="searchQuery"
                        type="text"
                        :placeholder="t('SearchTaskDialog.searchPlaceholder')"
                        class="th-input th-input-md flex-1 border-none outline-none"
                        @input="handleSearch"
                    />
                    <button
                        type="button"
                        class="th-btn th-btn-plain th-btn-sm th-btn-only-icon"
                        @click="closeDialog"
                    >
                        <PtIcon icon="SolidCloseBig" class="text-base fill-gray-400" />
                    </button>
                </div>
            </div>

            <!-- New Tasks Button -->
            <div class="px-6 pt-4">
                <button
                    type="button"
                    class="w-full flex items-center gap-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                    @click="handleNewTask"
                >
                    <PtIcon icon="SolidEdit" class="text-lg fill-gray-600" />
                    <span class="text-sm font-medium text-gray-700">{{ t('SearchTaskDialog.newTasks') }}</span>
                </button>
            </div>

            <!-- Task List -->
            <div class="px-6 py-4 max-h-[500px] overflow-y-auto">
                <template v-if="filteredTasks.length > 0">
                    <div
                        v-for="group in groupedTasks"
                        :key="group.date"
                        class="mb-6 last:mb-0"
                    >
                        <!-- Date Header -->
                        <div class="text-xs text-gray-400 mb-3">
                            {{ group.date }}
                        </div>

                        <!-- Task Items -->
                        <div class="space-y-2">
                            <div
                                v-for="task in group.tasks"
                                :key="task.session_id"
                                class="task-item p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                                @click="handleTaskClick(task)"
                            >
                                <div class="flex items-start gap-3">
                                    <!-- <PtIcon
                                        :icon="getTaskIcon(task.type)"
                                        class="text-base fill-gray-400 mt-0.5 flex-shrink-0"
                                    /> -->
                                    <div class="flex-1 min-w-0">
                                        <!-- <div class="text-sm font-medium text-gray-700 mb-1">
                                            {{ getTaskTypeName(task.type) }}
                                        </div> -->
                                        <div class="text-sm text-gray-600 line-clamp-2">
                                            {{ task.title }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
                <div v-else class="text-center py-12">
                    <PtIcon icon="SolidSearchLeft" class="text-4xl fill-gray-300 mx-auto mb-3" />
                    <p class="text-sm text-gray-400">{{ t('SearchTaskDialog.noTasksFound') }}</p>
                </div>
            </div>
        </form>
    </dialog>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { PtIcon } from 'patsnap-biz';
import { useI18n } from 'vue-i18n';
// import type { NavMenuItem, NAV_MENU_TYPE } from '../types';
import { cn, tw, en, jp } from '../infrastructure/locales';

const { t } = useI18n({
    messages: {
        cn, en, jp, tw,
    },
});

interface Props {
    tasks?: { title: string, session_id: string }[]
}

const props = withDefaults(defineProps<Props>(), {
    tasks: () => [],
});

const emit = defineEmits<{
    (e: 'task-click', task: { title: string, session_id: string }): void
    (e: 'new-task'): void
  (e: 'close'): void
  (e: 'search', query: string): void
}>();

const dialogRef = ref<HTMLDialogElement | null>(null);
const searchQuery = ref('');

const filteredTasks = computed(() => {
    if (!searchQuery.value.trim()) {
        return props.tasks;
    }
    const query = searchQuery.value.toLowerCase();
    return props.tasks.filter(task =>
        task.title?.toLowerCase().includes(query)
    );
});

const groupedTasks = computed(() => {
    const groups: { date: string; tasks: { title: string, session_id: string }[] }[] = [];
    const tasksByDate = new Map<string, { title: string, session_id: string }[]>();

    filteredTasks.value.forEach(task => {
        // 使用国际化的日期标签
        const date = t('SearchTaskDialog.today'); // 简化处理，实际应该根据创建时间分组
        if (!tasksByDate.has(date)) {
            tasksByDate.set(date, []);
        }
        tasksByDate.get(date)!.push(task);
    });

    tasksByDate.forEach((tasks, date) => {
        groups.push({ date, tasks });
    });

    return groups;
});

// function getTaskIcon(type: NAV_MENU_TYPE): string {
//     // 根据任务类型返回对应图标
//     const iconMap: Record<string, string> = {
//         SEEK_SEARCH: 'SolidSearchLeft',
//         LS_DOCUMENT_QA: 'SolidFileDocument',
//         LS_CHAT: 'SolidFlask',
//     };
//     return iconMap[type] || 'SolidFileDocument';
// }

// function getTaskTypeName(type: NAV_MENU_TYPE): string {
//     return t(`HistoryType.${type}`) || type;
// }

function closeDialog() {
    dialogRef.value?.close();
    emit('close');
}

function openDialog() {
    dialogRef.value?.showModal();
}

function handleSearch() {
    // 搜索逻辑已通过 computed 实现
    emit('search', searchQuery.value.trim());
}

function handleTaskClick(task: { title: string, session_id: string }) {
    emit('task-click', task);
    closeDialog();
}

function handleNewTask() {
    emit('new-task');
    closeDialog();
}

defineExpose({
    openDialog,
    closeDialog,
});
</script>

<style lang="scss">
.search-task-dialog {
    z-index: 900 !important;

    &::backdrop {
        z-index: 899 !important;
        background-color: rgba(0, 0, 0, 0.5);
    }

    .th-modal-box {

        @media (max-width: 1024px) {
            margin-left: 0;
        }
    }
}

.task-item {
    &:hover {
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    }
}

.line-clamp-2 {
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>
